import { HttpError } from 'react-admin';
import type { DataProvider, GetListParams } from 'react-admin';

import { requirePartner } from '@src/providers/dataProvider/shared';

import { fetchItemAdjustedPrices } from './fetchItemAdjustedPrices';
import { filterOverrideRows } from './overrideFilters';
import type { OverrideFilters } from './overrideFilters';
import type { YAdjustedRow, YAdjustedRowWithId } from './types';

interface ItemAdjustedPricesDataProvider {
  getList: (
    resource: string,
    params: GetListParams,
  ) => Promise<{ data: YAdjustedRowWithId[], total: number }>;
  getOne: () => Promise<never>;
  getMany: () => Promise<never>;
  getManyReference: () => Promise<never>;
  create: () => Promise<never>;
  update: () => Promise<never>;
  updateMany: () => Promise<never>;
  delete: () => Promise<never>;
  deleteMany: () => Promise<never>;
}

const toFilters = (params: GetListParams): OverrideFilters => {
  const filter = params.filter ?? {};
  return {
    searchQuery: filter.searchQuery || undefined,
    department: filter.department || undefined,
    category: filter.category || undefined,
    color: filter.color || undefined,
    sizeCode: filter.sizeCode || undefined,
    yadjustedCode: filter.yadjustedCode || undefined,
  };
};

// React-admin requires an `id` field on every record. Y-adjusted rows have no
// server-side ID — synthesize from the unique key.
const withIds = (rows: Omit<YAdjustedRowWithId, 'id'>[]): YAdjustedRowWithId[] =>
  rows.map((row, index) => ({
    ...row,
    id: `${row.parentSku ?? ''}-${row.skuSubcode ?? ''}-${row.color ?? ''}-${index}`,
  }));

const hasActiveFilter = (filters: OverrideFilters): boolean =>
  Object.values(filters).some(value => value != null && value !== '');

// Batch size for pulling the full override set when a filter is active.
const FETCH_ALL_PAGE_SIZE = 500;

/**
 * Fetch every override row for a partner, in batches. Needed because the
 * yadjustedPricing API has no server-side filtering and paginates before we
 * could filter — so a filtered list must be assembled and paginated client-side.
 * (If the API ever gains server-side filtering, drop this and filter there.)
 */
const fetchAllOverrideRows = async (partnerId: number): Promise<YAdjustedRow[]> => {
  const firstPage = await fetchItemAdjustedPrices({ partnerId, page: 1, pageSize: FETCH_ALL_PAGE_SIZE });
  const rows = [...firstPage.rows];
  const totalPages = Math.ceil(firstPage.count / FETCH_ALL_PAGE_SIZE);
  for (let page = 2; page <= totalPages; page += 1) {
    const next = await fetchItemAdjustedPrices({ partnerId, page, pageSize: FETCH_ALL_PAGE_SIZE });
    rows.push(...next.rows);
  }
  return rows;
};

/**
 * Data provider for itemAdjustedPrices resource.
 *
 * Read-only — only getList is implemented. Uses offset pagination (page/pageSize),
 * unlike pricingRules which uses cursor-based pagination (first/after).
 *
 * The yadjustedPricing API has no server-side filtering. With no filter we let
 * the server paginate; when a filter is active we fetch the whole set and
 * filter/paginate client-side, so `total` and the reachable pages reflect the
 * filtered result rather than the unfiltered server count.
 */
const itemAdjustedPricesDataProviderImpl: ItemAdjustedPricesDataProvider = {
  getList: async (_resource, params) => {
    try {
      const { page = 1, perPage = 50 } = params.pagination || {};
      const filters = toFilters(params);
      const partner = requirePartner();

      // Fast path: no filter → let the server paginate the page directly.
      if (!hasActiveFilter(filters)) {
        const result = await fetchItemAdjustedPrices({
          partnerId: partner.id,
          page,
          pageSize: perPage,
        });
        return { data: withIds(result.rows), total: result.count };
      }

      // Filtered: assemble the full set, filter, then slice for the requested
      // page. `withIds` runs over the whole filtered set so synthetic ids stay
      // unique and stable across pages.
      const allRows = await fetchAllOverrideRows(partner.id);
      const filtered = withIds(filterOverrideRows(allRows, filters));
      const start = (page - 1) * perPage;

      return {
        data: filtered.slice(start, start + perPage),
        total: filtered.length,
      };
    } catch (error) {
      if (error instanceof HttpError) throw error;
      console.error('itemAdjustedPricesDataProvider.getList failed:', {
        error: error instanceof Error ? error.message : error,
      });
      const status = (error as { status?: number }).status || 500;
      throw new HttpError('Failed to load y-adjusted pricing.', status);
    }
  },

  getOne: async () => { throw new Error('getOne not implemented for itemAdjustedPrices resource'); },
  getMany: async () => { throw new Error('getMany not implemented for itemAdjustedPrices resource'); },
  getManyReference: async () => { throw new Error('getManyReference not implemented for itemAdjustedPrices resource'); },
  create: async () => { throw new Error('create not implemented for itemAdjustedPrices resource'); },
  update: async () => { throw new Error('update not implemented for itemAdjustedPrices resource'); },
  updateMany: async () => { throw new Error('updateMany not implemented for itemAdjustedPrices resource'); },
  delete: async () => { throw new Error('delete not implemented for itemAdjustedPrices resource'); },
  deleteMany: async () => { throw new Error('deleteMany not implemented for itemAdjustedPrices resource'); },
};

// Cast to DataProvider for react-admin compatibility.
// React-admin's DataProvider methods are generic, which prevents type-safe
// single-resource providers. We define proper types above and cast here.
export const itemAdjustedPricesDataProvider = itemAdjustedPricesDataProviderImpl as unknown as DataProvider;
export default itemAdjustedPricesDataProvider;
